/**
 * Landing Page + OAuth Callback
 * Public marketing page when unauthenticated. Auth0 code exchange when ?code= present.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Divider, Link, Paper, Typography } from '@mui/material';
import { Celebration, Event, Hotel, Museum, CloudUpload, Tune, PhoneAndroid, ArrowForward } from '@mui/icons-material';
import { useAuth } from '../lib/auth';
import { CONCIERGE_API_URL } from '../services/concierge-api-client';
import TripsbyStudioLogo from '../components/TripsbyStudioLogo';

export default function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, isAuthenticated, redirectToLogin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [exchanging, setExchanging] = useState(false);
  const exchangeAttempted = useRef(false);
  const code = searchParams.get('code');

  useEffect(() => { if (isAuthenticated && !code) navigate('/concierges', { replace: true }); }, [isAuthenticated, navigate, code]);

  useEffect(() => {
    if (!code || exchangeAttempted.current || isAuthenticated) return;
    exchangeAttempted.current = true;
    const run = async () => {
      setExchanging(true); setError(null);
      try {
        const redirectUri = `${window.location.origin}/login`;
        const resp = await fetch(`${CONCIERGE_API_URL}/auth/token/${encodeURIComponent(code)}?redirect_uri=${encodeURIComponent(redirectUri)}`, { headers: { Accept: 'application/json' } });
        if (!resp.ok) { const d = await resp.json().catch(() => ({ detail: resp.statusText })); throw new Error(d.detail || d.error || 'Failed'); }
        const data = await resp.json();
        if (!data.token) throw new Error('No token received');
        login(data.token, data.user, data.expiresIn);
        try {
          const ir = await fetch(`${CONCIERGE_API_URL}/auth/login`, { method: 'POST', headers: { Authorization: `Bearer ${data.token}`, 'Content-Type': 'application/json' } });
          if (ir.ok) { const d = await ir.json(); if (d.subscription?.entitlements) localStorage.setItem('studio_entitlements', JSON.stringify(d.subscription.entitlements)); }
        } catch {}
        navigate('/concierges', { replace: true });
      } catch (err) { setError(err instanceof Error ? err.message : 'Authentication failed'); } finally { setExchanging(false); }
    };
    run();
  }, [code, isAuthenticated, login, navigate]);

  if (code) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
      {error ? (<><Alert severity="error" sx={{ maxWidth: 400 }}>{error}</Alert><Button variant="outlined" onClick={redirectToLogin}>Try Again</Button></>) : (<><CircularProgress sx={{ color: '#569469' }} /><Typography color="text.secondary">Signing you in...</Typography></>)}
    </Box>
  );

  const useCases = [
    { icon: <Celebration sx={{ fontSize: 28 }} />, title: 'Weddings', desc: 'Personal concierge for venue info, schedules, and local tips' },
    { icon: <Event sx={{ fontSize: 28 }} />, title: 'Events', desc: 'Help attendees navigate your conference with AI' },
    { icon: <Hotel sx={{ fontSize: 28 }} />, title: 'Hotels', desc: 'Instant answers about your property and amenities' },
    { icon: <Museum sx={{ fontSize: 28 }} />, title: 'Museums', desc: 'Interactive guide for your exhibits and collections' },
  ];
  const steps = [
    { icon: <CloudUpload sx={{ fontSize: 32, color: '#569469' }} />, title: 'Upload your documents', desc: 'PDFs, menus, schedules, FAQs — anything guests might ask about' },
    { icon: <Tune sx={{ fontSize: 32, color: '#569469' }} />, title: 'Configure the persona', desc: 'Set the tone, greeting, and personality to match your brand' },
    { icon: <PhoneAndroid sx={{ fontSize: 32, color: '#569469' }} />, title: 'Share your number', desc: 'Get a dedicated SMS number your guests can text anytime' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAF9' }}>
      <Box sx={{ px: 4, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1200, mx: 'auto' }}>
        <TripsbyStudioLogo width={160} />
        <Button variant="outlined" onClick={redirectToLogin} sx={{ borderRadius: 3, borderColor: '#3C5945', color: '#3C5945', '&:hover': { bgcolor: '#3C5945', color: 'white' } }}>Sign In</Button>
      </Box>
      <Box sx={{ bgcolor: '#F8FAF9', pt: 10, pb: 8 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ color: '#569469', fontWeight: 600, letterSpacing: 2, fontSize: 12 }}>TRIPSBY STUDIO</Typography>
          <Typography variant="h2" fontWeight={800} sx={{ color: '#09090A', mt: 1, lineHeight: 1.1 }}>Build your own{' '}<Box component="span" sx={{ color: '#569469' }}>AI concierge.</Box></Typography>
          <Typography sx={{ color: '#676970', mt: 2, fontSize: 18, maxWidth: 500, lineHeight: 1.6 }}>Upload your documents, configure the personality, and give your guests a dedicated SMS number they can text for instant, grounded answers.</Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button variant="contained" size="large" onClick={redirectToLogin} sx={{ borderRadius: 3, bgcolor: '#3C5945', px: 4, '&:hover': { bgcolor: '#2E4635' } }}>Get Started</Button>
            <Button variant="outlined" size="large" endIcon={<ArrowForward />} sx={{ borderRadius: 3, borderColor: '#3C5945', color: '#3C5945' }} href="#how-it-works">Learn More</Button>
          </Box>
        </Container>
      </Box>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} sx={{ color: '#09090A', mb: 4 }}>What can you build?</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3 }}>
          {useCases.map(uc => (
            <Paper key={uc.title} elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #E5E7EB', transition: 'all 0.2s', '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' } }}>
              <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: '#569469' }}>{uc.icon}</Box>
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>{uc.title}</Typography>
              <Typography variant="body2" color="text.secondary">{uc.desc}</Typography>
            </Paper>
          ))}
        </Box>
      </Container>
      <Divider />
      <Container id="how-it-works" maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} sx={{ textAlign: 'center', mb: 1 }}>How it works</Typography>
        <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 5 }}>Three simple steps to launch your AI concierge</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 4 }}>
          {steps.map((s, i) => (
            <Box key={i} sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>{s.icon}</Box>
              <Typography variant="overline" sx={{ color: '#569469', fontWeight: 600 }}>Step {i + 1}</Typography>
              <Typography fontWeight={600} sx={{ mt: 0.5 }}>{s.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{s.desc}</Typography>
            </Box>
          ))}
        </Box>
      </Container>
      <Divider />
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" fontWeight={700}>Ready to build?</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>Create your first AI concierge in minutes — no coding required.</Typography>
        <Button variant="contained" size="large" onClick={redirectToLogin} sx={{ borderRadius: 3, bgcolor: '#3C5945', px: 5, py: 1.5, '&:hover': { bgcolor: '#2E4635' } }}>Get Started Free</Button>
      </Box>
      <Box sx={{ bgcolor: '#FAFAF9', borderTop: '1px solid #E5E7EB', py: 4, px: 4 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">Tripsby Studio by <Link href="https://tripsby.ai" target="_blank" underline="hover" color="inherit" fontWeight={600}>Tripsby.ai</Link></Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Link href="https://tripsby.ai/privacy-policy" target="_blank" underline="hover" variant="body2" color="text.secondary">Privacy</Link>
              <Link href="https://tripsby.ai/terms-of-service" target="_blank" underline="hover" variant="body2" color="text.secondary">Terms</Link>
              <Link href="mailto:concierge@tripsby.ai" underline="hover" variant="body2" color="text.secondary">Contact</Link>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>Copyright &copy; Tripsby {new Date().getFullYear()}. All Rights Reserved.</Typography>
        </Container>
      </Box>
    </Box>
  );
}
